/**
 * Constants.ts
 *
 * A module for all UI constants.
 */

/**
 * HTML attribute names
 */
export class AttributeName {
	public static Alt = "alt";
	public static AriaLabel = "aria-label";
	public static AriaSelected = "aria-selected";
	public static AriaOrientation = "aria-orientation";
	public static AriaExpanded = "aria-expanded";
	public static AriaControls = "aria-controls";
	public static AriaHasPopups = "aria-haspopup";
	public static AriaModal = "aria-modal";
	public static AriaLabelledBy = "aria-labelledby";
	public static AriaDescribedBy = "aria-describedby";
	public static AriaLevel = "aria-level";
	public static Class = "class";
	public static DataHtml2CanvasIgnore = "data-html2canvas-ignore";
	public static Disabled = "disabled";
	public static Fill = "fill";
	public static For = "for";
	public static Preview = "preview";
	public static Form = "form";
	public static Height = "height";
	public static HRef = "href";
	public static Id = "id";
	public static MaxLength = "maxlength";
	public static Name = "name";
	public static Placeholder = "placeholder";
	public static Points = "points";
	public static Rel = "rel";
	public static Role = "role";
	public static Source = "src";
	public static Stroke = "stroke";
	public static Style = "style";
	public static TabIndex = "tabindex";
	public static Target = "target";
	public static Transform = "transform";
	public static Type = "type";
	public static Value = "value";
	public static ViewBox = "viewBox";
	public static Width = "width";
	public static xlinkHref = "xlink:href";
	public static X = "x";
	public static Y = "y";
}

/**
 * HTML attribute values
 */
export class AttributeValue {
	public static BlankWindow = "_blank";
	public static Button = "button";
	public static Dialog = "dialog";
	public static Tab = "tab";
	public static TabList = "tablist";
	public static TabPanel = "tabpanel";
	public static Checkbox = "checkbox";
	public static Checked = "checked";
	public static False = "false";
	public static Zero = "0";
	public static Polite = "polite";
	public static Radio = "radio";
	public static Submit = "submit";
	public static ScreenshotPreview = "ScreenshotPreview";
	public static Text = "text";
	public static TextAreaMaxLength = "1000";
	public static True = "true";
	public static Unchecked = "unchecked";
	public static NoReferrer = "noreferrer";
	public static AriaOrientationValueVertical = "vertical";
}

/**
 * URL Parameters
 */
export class UrlParameters {
	public static CLCID = "CLCID";
}

/**
 * CSS classes
 */
export class Classes {
	public static AriaLiveTemplate = "obf-AriaLiveTemplate";
	public static CheckBox = "obf-CheckBox";
	public static FontTitle = "obf-FontTitle";
	public static CloseButton = "obf-CloseButton";
	public static FontSubtitle = "obf-FontSubtitle";
	public static FontText = "obf-FontText";
	public static FontSubText = "obf-FontSubText";
	public static FontSubSubText = "obf-FontSubSubText";
	public static Hidden = "obf-Hidden";
	public static Link = "obf-Link";
	public static MarginLeft60px = "obf-MarginLeft60px";
	public static MarginLeft180px = "obf-MarginLeft180px";
	public static NarrowLayout = "obf-NarrowLayout";
	public static PrivacyStatementLinkDiv = "obf-PrivacyStatementLinkDiv";
	public static PrivacyStatementLinkDivEudbUx = "obf-PrivacyStatementLinkDivEudbUx";
	public static Rtl = "obf-Rtl";
	public static Spinner = "obf-Spinner";
	public static SpinnerCircle = "obf-SpinnerCircle";
	public static SubmitButton = "obf-SubmitButton";
	public static CancelButton = "obf-CancelButton";
	public static TextInput = "obf-TextInput";
	public static Visible = "obf-Visible";
	public static RequiredAsterisk = "obf-RequiredAsterisk";

	// rating control
	public static Rating = "obf-Rating";
	public static RatingGraphic = "obf-RatingGraphic";
	public static RatingGraphicFilled = "obf-RatingGraphic-Filled";

	// choice group control
	public static ChoiceGroup = "obf-ChoiceGroup";
	public static ChoiceGroupIcon = "obf-ChoiceGroupIcon";

	// region BellyBand

	public static OverallAnchor = "obf-OverallAnchor";
	public static OverallAnchorActive = "obf-OverallAnchorActive";
	public static OverallImage = "obf-OverallImage";
	public static OverallText = "obf-OverallText";
	public static SingleLayout = "obf-SingleLayout";
	public static ShowRightBorder = "obf-ShowRightBorder";
	public static SlideLeft = "obf-slideLeft";
	public static TextAlignLeft = "obf-TextAlignLeft";
	public static FormContainer = "obf-FormContainer";
	public static FormWideContainer = "obf-FormWideContainer";

	// region ThanksPanel
	public static ThanksPanelTitle = "obf-ThanksPanelTitle";
	public static ThanksPanelMessage = "obf-ThanksPanelMessage";

	// form classes
	public static FormQuestionMiddleText = "obf-FormQuestionMiddleText";
	public static FormMiddleText = "obf-FormMiddleText";
	public static FormCategoriesDropdown = "obf-FormCategoriesDropdown";
	public static FormComment = "obf-FormComment";
	public static FormRatingContainer = "obf-FormRatingContainer";
	public static FormRatingLabel = "obf-FormRatingLabel";
	public static FormRating = "obf-FormRating";
	public static FormEmailContainer = "obf-FormEmailContainer";
	public static FormEmailInput = "obf-FormEmailInput";
	public static FormEmailTextBox = "obf-FormEmailTextBox";
	public static FormEmailCheckBox = "obf-FormEmailCheckBox";
	public static FormEmailLabel = "obf-FormEmailLabel";
	public static FormBottomContainer = "obf-FormBottomContainer";
	public static FormSubmitButtonContainer = "obf-FormSubmitButtonContainer";
	public static FormScreenshotContainer = "obf-FormScreenshotContainer";
	public static FormScreenshotLabel = "obf-FormScreenshotLabel";
	public static FormScreenshotCheckbox = "obf-FormScreenshotCheckbox";
	public static FormScreenshotPreview = "obf-FormScreenshotPreview";
	public static FormPrivacyConsentText = "obf-TFormPrivacyConsentText";
	// email textbox classes
	public static TFormEmailCheckbox = "obf-TFormEmailCheckbox";
	public static TFormEmailLabel = "obf-TFormEmailLabel ";

	// email checkbox classes
	public static EmailCheckBoxLabel = "obf-EmailCheckBoxLabel";

	// endregion

	// region Toast

	public static Toast = "obf-Toast";
	public static ToastZoom = "obf-ToastZoom";

	// endregion
}

/**
 * HTML element ids
 */
export class IDs {
	// region BellyBand
	public static CloseButton = "obf-CloseButton";
	public static ColumnSeparatorDiv = "obf-ColumnSeparatorDiv";
	public static OverallAnchorsContainer = "obf-OverallAnchorsContainer";
	public static OverallFrownAnchor = "obf-OverallFrownAnchor";
	public static OverallFrownDiv = "obf-OverallFrownDiv";
	public static OverallFrownImage = "obf-OverallFrownImage";
	public static OverallFrownText = "obf-OverallFrownText";
	public static OverallSmileAnchor = "obf-OverallSmileAnchor";
	public static OverallSmileDiv = "obf-OverallSmileDiv";
	public static OverallSmileImage = "obf-OverallSmileImage";
	public static OverallSmileText = "obf-OverallSmileText";
	public static OverallIdeaAnchor = "obf-OverallIdeaAnchor";
	public static OverallIdeaDiv = "obf-OverallIdeaDiv";
	public static OverallIdeaImage = "obf-OverallIdeaImage";
	public static OverallIdeaText = "obf-OverallIdeaText";
	public static OverallBugAnchor = "obf-OverallBugAnchor";
	public static OverallBugDiv = "obf-OverallBugDiv";
	public static OverallBugImage = "obf-OverallBugImage";
	public static OverallBugText = "obf-OverallBugText";
	public static QuestionLeftText = "obf-QuestionLeftText";
	public static LeftFormContainer = "obf-LeftFormContainer";
	public static LeftPanelContainer = "obf-LeftPanelContainer";
	public static MainContainer = "obf-MainContainer";
	public static MainContentHolder = "obf-MainContentHolder";
	public static MiddleFormContainer = "obf-MiddleFormContainer";
	public static OverlayBackground = "obf-OverlayBackground";
	public static PrivacyStatementLink = "obf-PrivacyStatementLink";
	public static PrivacyConsentText = "obf-PrivacyConsentText";
	public static FirstTabbable = "obf-FirstTabbable";
	public static LastTabbable = "obf-LastTabbable";
	public static EmailCheckBox = "obf-EmailCheckBox";
	public static RequiredAsterisk = "obf-RequiredStar";

	// Single form ids
	public static SingleFormContainer = "obf-SingleFormContainer";
	public static SingleFormQuestionMiddleText = "obf-SingleFormQuestionMiddleText";
	public static SingleFormCategoriesDropdown = "obf-SingleFormCategoriesDropdown";
	public static SingleFormComment = "obf-SingleFormComment";
	public static SingleFormEmailInput = "obf-SingleFormEmailInput";
	public static SingleFormRating = "obf-SingleFormRating";
	public static SingleFormScreenshotCheckbox = "obf-SingleFormScreenshotCheckbox";
	public static SingleFormScreenshotPreview = "obf-SingleFormScreenshotPreview";
	public static SingleFormSubmitButton = "obf-SingleFormSubmitButton";
	public static SingleFormCancelButton = "obf-SingleFormCancelButton";
	public static SingleFormSubmitButtonSpinner = "obf-SingleFormSubmitButtonSpinner";

	// Basic form ids
	public static BasicFormContainer = "obf-BasicFormContainer";
	public static BasicFormQuestionMiddleText = "obf-BasicFormQuestionMiddleText";
	public static BasicFormCategoriesDropdown = "obf-BasicFormCategoriesDropdown";
	public static BasicFormComment = "obf-BasicFormComment";
	public static BasicFormEmailInput = "obf-BasicFormEmailInput";
	public static BasicFormRating = "obf-BasicFormRating";
	public static BasicFormScreenshotCheckbox = "obf-BasicFormScreenshotCheckbox";
	public static BasicFormScreenshotPreview = "obf-BasicFormScreenshotPreview";
	public static BasicFormSubmitButton = "obf-BasicFormSubmitButton";
	public static BasicFormCancelButton = "obf-BasicFormCancelButton";
	public static BasicFormSubmitButtonSpinner = "obf-BasicFormSubmitButtonSpinner";

	// Thanks Panel ids
	public static ThanksPanelContainer = "obf-ThanksPanelContainer";
	public static ThanksPanelCloseButton = "obf-ThanksPanelCloseButton";
	public static ThanksPanelDiscussion = "obf-ThanksPanelDiscussion";
	public static ThanksPanelVerticalContainer = "obf-ThanksPanelVerticalContainer";
	public static ThanksPanelInnerContainer = "obf-ThanksPanelInnerContainer";
	public static ThanksPanelParagraph = "obf-ThanksPanelParagraph";

	// Feedback Portal Idea Id
	public static FeedbackPortalIdeaContainer = "obf-FeedbackPortalIdeaContainer";
	public static OverallFeedbackPortalIdeaAnchor = "obf-OverallFeedbackPortalIdeaAnchor";

	// endregion

	// region Toast
	public static ToastContainer = "obf-ToastContainer";
	public static ToastCancel = "obf-ToastCancel";

	// Prompt ids
	public static TPromptContainer = "obf-TPromptContainer";
	public static TPromptTitle = "obf-TPromptTitle";
	public static TPromptText = "obf-TPromptText";

	// Survey ids
	public static TFormContainer = "obf-TFormContainer";
	public static TFormContainerLabel = "obf-TFormContainerLabel";
	public static TFormTitle = "obf-TFormTitle";
	public static TFormRating = "obf-TFormRating";
	public static TFormRatingQuestion = "obf-TFormRatingQuestion";
	public static TFormCommentQuestion = "obf-TFormCommentQuestion";
	public static TFormComment = "obf-TFormComment";
	public static TFormEmailTextBox = "obf-TFormEmailTextBox";
	public static TFormEmailCheckBox = "obf-TFormEmailCheckBox";
	public static TFormEmailLabel = "obf-TFormEmailLabel";
	public static TFormSubmitButton = "obf-TFormSubmitButton";
	public static TFormCancelButton = "obf-TFormCancelButton";
	public static TFormSubmitButtonSpinner = "obf-TFormSubmitButtonSpinner";
	public static TFormSubmitButtonContainer = "obf-TFormSubmitButtonContainer";

	// endregion
}

/**
 * Keys
 */
export class Keys {
	public static Esc = 27;
	public static Tab = 9;
}

/**
 * HTML tags
 */
export class Tags {
	public static Anchor = "A";
	public static Button = "button";
	public static Defs = "defs";
	public static Div = "div";
	public static FieldSet = "fieldset";
	public static Form = "form";
	public static Img = "img";
	public static Input = "input";
	public static Label = "label";
	public static Legend = "legend";
	public static Option = "option";
	public static Polygon = "polygon";
	public static Select = "select";
	public static Span = "span";
	public static Svg = "svg";
	public static TextArea = "textarea";
	public static Use = "use";
}

/**
 * HTML roles
 */
export class Roles {
	public static Button = "Button";
}

/**
 * URLs
 */
export class Urls {
	/**
	 * Link to the feedback Privacy Statement
	 */
	public static PrivacyStatementLink = "https://go.microsoft.com/fwlink/?LinkId=521839";
}

/**
 * prompt constants
 */
export class PromptConstants {
	public static No = "1";
	public static Yes = "2";
}
